import { scheduleClipFades } from './clipEdits';
/**
 * PlaybackScheduler.js — Section 5.2
 *
 * Schedules clip playback using AudioBufferSourceNode.start()
 * with precise timing from AudioContext.currentTime.
 *
 * 7-step playback flow:
 * 1. User presses Play
 * 2. Capture startTime = AudioContext.currentTime
 * 3. Iterate tracks (skip muted, respect solo)
 * 4. For each track's clips, calculate clipStartTime
 * 5. Create AudioBufferSourceNode per clip
 * 6. Connect each source into the persistent shared mix graph track input
 * 7. source.start(clipStartTime, 0, clipDuration)
 *
 * Playhead updates via requestAnimationFrame reading AudioContext.currentTime.
 *
 * Public API:
 *   startPlayback(tracks, bpm, totalBars, fromBar, playheadCb, endCb)
 *   pausePlayback()          — stops sources, preserves current bar for resume
 *   resumePlayback(tracks, bpm, totalBars, playheadCb, endCb)
 *   stopPlayback()           — stops sources, resets playhead to 0
 *   seekToBar(bar, tracks, bpm, totalBars, playheadCb, endCb)
 *   setTrackVolume / setTrackPan / updateSoloMute / cleanup / getIsPlaying
 *   getPausedBar()           — returns the bar where playback was paused
 */

import { getAudioContext, getMasterGain, getTrackInput, secondsPerBar, updateMixGraph } from './AudioEngine';
import { getCached, hasCached } from './BufferCache';
import { playableBuffer } from './stretchedBuffers';
import { clipPlaybackWindow } from './clipWindow';
import { isAudioEligible } from './classAudio';

// Active playback state
let isPlaying = false;
let isPaused = false;
let contextStartTime = 0;      // AudioContext.currentTime when play was pressed
let playbackStartBar = 0;      // Bar offset (for resume / jump-to-bar)
let pausedAtBar = 0;           // Bar where pause happened
let currentBpm = 90;
let currentTotalBars = 32;
let activeSources = [];         // All active AudioBufferSourceNode references
let currentTracks = [];
let rafId = null;               // requestAnimationFrame ID
let onPlayheadUpdate = null;    // Callback: (currentBar: number) => void
let onPlaybackEnd = null;       // Callback: () => void
let activeLoop = null;
let scheduledStartContextTime = 0;

/** Shared-clock click scheduler used for count-ins and transport playback. */
export function scheduleMetronomeBeats(ctx, destination, { startAt, beatSeconds, firstBeat = 0, beats = 4 } = {}) {
  const sources = [];
  for (let index = 0; index < beats; index += 1) {
    const beat = firstBeat + index;
    const at = Number(startAt) + index * Number(beatSeconds);
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.frequency.setValueAtTime(beat % 4 === 0 ? 1200 : 850, at);
    gain.gain.setValueAtTime(0.18, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + 0.045);
    oscillator.connect(gain); gain.connect(destination);
    oscillator.start(at); oscillator.stop(at + 0.05);
    sources.push(oscillator);
  }
  return sources;
}

/**
 * Check that all required AudioBuffers are cached before playback.
 * Returns { ready: boolean, missing: string[] }
 */
export function checkBuffersReady(tracks) {
  const missing = [];
  for (const track of tracks) {
    if (!isAudioEligible(track)) continue;
    for (const clip of track.clips) {
      if (!hasCached(clip.loop_id)) {
        missing.push(clip.loop_id);
      }
    }
  }
  return { ready: missing.length === 0, missing: [...new Set(missing)] };
}

/**
 * Schedule all clips for playback starting from a specific bar.
 *
 * @param {Array} tracks - Track objects with clips
 * @param {number} bpm - Beats per minute
 * @param {number} totalBars - Total bars in the project
 * @param {number} fromBar - Bar to start playback from (0 = beginning)
 * @param {Function} playheadCallback - (currentBar) => void
 * @param {Function} endCallback - () => void called when playback reaches end
 * @throws {Error} if required AudioBuffers are not cached
 */
export function startPlayback(tracks, bpm, totalBars, fromBar, playheadCallback, endCallback, loop = null, options = {}) {
  // Pre-fetch gating: warn about missing buffers but allow playback of available clips
  const { ready, missing } = checkBuffersReady(tracks);
  if (!ready) {
    console.warn(`Playback: missing AudioBuffers for loops: ${missing.join(', ')}. Those clips will be silent.`);
  }

  if (isPlaying) {
    teardownSources();
  }

  const ctx = getAudioContext();
  const requestedStartAt = typeof options === 'number' ? options : options?.startAt;
  const startAt = Number.isFinite(Number(requestedStartAt)) ? Number(requestedStartAt) : ctx.currentTime;
  const metronome = typeof options === 'object' && Boolean(options?.metronome);
  const spb = secondsPerBar(bpm);
  const loopRegion = loop?.enabled ? loop.region : null;
  const requestedBar = fromBar || 0;
  // Inside the loop (e.g. an edit rescheduling mid-loop) keep going from the playhead;
  // only a start outside the region jumps to the loop start.
  const insideLoop = loopRegion && requestedBar >= loopRegion.startBar && requestedBar < loopRegion.endBar;
  const effectiveFromBar = loopRegion && !insideLoop ? loopRegion.startBar : requestedBar;
  const startOffset = effectiveFromBar * spb;
  const totalDuration = totalBars * spb;
  const remainingDuration = totalDuration - startOffset;

  isPlaying = true;
  isPaused = false;
  playbackStartBar = effectiveFromBar;
  scheduledStartContextTime = startAt;
  contextStartTime = startAt - startOffset; // Shift so elapsed maps to correct bar
  currentBpm = bpm;
  currentTracks = tracks;
  updateMixGraph(tracks, bpm);
  currentTotalBars = totalBars;
  activeSources = [];
  onPlayheadUpdate = playheadCallback;
  onPlaybackEnd = endCallback;
  activeLoop = loopRegion ? { startBar: loopRegion.startBar, endBar: loopRegion.endBar } : null;

  const scheduleClipWindow = (track, clip, window, passStartAt) => {
    const buffer = playableBuffer(clip, bpm, track);
    if (!buffer || !window) return;
    const trackInput = getTrackInput(track.id);
    if (!trackInput) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const clipGain = ctx.createGain();
    source.connect(clipGain);
    clipGain.connect(trackInput);
    const startAt = passStartAt + window.when;
    scheduleClipFades(clipGain.gain, clip, window, startAt, bpm);
    source.start(startAt, window.bufferOffset, window.duration);
    activeSources.push(source);
  };

  if (activeLoop) {
    const loopStartSec = activeLoop.startBar * spb;
    const loopEndSec = activeLoop.endBar * spb;
    const loopDuration = loopEndSec - loopStartSec;
    const t0 = startAt;
    // Pass 0 may be partial (we can start mid-loop); every later pass is a full loop.
    // Pass k (k >= 1) starts at t0 + firstPassSec + (k - 1) * loopDuration.
    const firstPassSec = loopEndSec - startOffset;
    const passStart = (k) => (k === 0 ? t0 : t0 + firstPassSec + (k - 1) * loopDuration);
    const schedulePass = (k) => {
      const fromSec = k === 0 ? startOffset : loopStartSec;
      for (const track of tracks) for (const clip of isAudioEligible(track) ? track.clips : []) {
        scheduleClipWindow(track, clip, clipPlaybackWindow(clip, bpm, fromSec, loopEndSec), passStart(k));
      }
      if (metronome) {
        const beatSeconds = 60 / bpm;
        activeSources.push(...scheduleMetronomeBeats(ctx, getMasterGain(), {
          startAt: passStart(k), beatSeconds, firstBeat: Math.round(fromSec / beatSeconds), beats: Math.max(0, Math.round((loopEndSec - fromSec) / beatSeconds)),
        }));
      }
      // Two passes are always queued ahead. When pass k begins, queue pass k + 2, so the
      // wrap is sample-accurate and no pass is ever scheduled twice.
      const marker = ctx.createBufferSource();
      marker.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      marker.connect(ctx.destination);
      marker.start(passStart(k));
      marker.onended = () => {
        if (isPlaying && activeLoop) schedulePass(k + 2);
      };
      activeSources.push(marker);
    };
    schedulePass(0);
    schedulePass(1);
  } else {
    for (const track of tracks) for (const clip of isAudioEligible(track) ? track.clips : []) {
      scheduleClipWindow(track, clip, clipPlaybackWindow(clip, bpm, startOffset), startAt);
    }
    if (metronome) activeSources.push(...scheduleMetronomeBeats(ctx, getMasterGain(), {
      startAt, beatSeconds: 60 / bpm, firstBeat: Math.floor(effectiveFromBar * 4), beats: Math.max(0, Math.ceil((totalBars - effectiveFromBar) * 4)),
    }));
  }

  // Schedule auto-stop at end of arrangement
  if (!activeLoop && remainingDuration > 0) {
    const endTime = startAt + remainingDuration;
    const silentSource = ctx.createBufferSource();
    silentSource.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    silentSource.connect(ctx.destination);
    silentSource.start(endTime);
    silentSource.onended = () => {
      if (isPlaying) {
        stopPlayback();
        onPlaybackEnd?.();
      }
    };
    activeSources.push(silentSource);
  }

  // Start playhead RAF loop
  startPlayheadLoop(bpm, totalBars);
}

/**
 * Pause playback — stops all sources but preserves the current bar position.
 * Call resumePlayback() to continue from where you paused.
 */
export function pausePlayback() {
  if (!isPlaying) return;

  const ctx = getAudioContext();
  const spb = secondsPerBar(currentBpm);
  const elapsed = Math.max(playbackStartBar * spb, ctx.currentTime - contextStartTime);
  pausedAtBar = activeLoop
    ? activeLoop.startBar + ((elapsed / spb - activeLoop.startBar) % (activeLoop.endBar - activeLoop.startBar) + (activeLoop.endBar - activeLoop.startBar)) % (activeLoop.endBar - activeLoop.startBar)
    : elapsed / spb;

  isPaused = true;
  isPlaying = false;
  teardownSources();

  // Update playhead to paused position (don't reset to 0)
  onPlayheadUpdate?.(pausedAtBar);
}

/**
 * Resume playback from the paused bar position.
 */
export function resumePlayback(tracks, bpm, totalBars, playheadCallback, endCallback) {
  if (!isPaused) return;
  startPlayback(tracks, bpm, totalBars, pausedAtBar, playheadCallback, endCallback);
  isPaused = false;
}

/**
 * Stop all playback. Disconnect all sources. Reset playhead to bar 0.
 */
export function stopPlayback() {
  isPlaying = false;
  isPaused = false;
  pausedAtBar = 0;
  activeLoop = null;

  teardownSources();

  // Reset playhead to 0
  onPlayheadUpdate?.(0);
}

/**
 * Jump to a specific bar and begin playback from there.
 * If currently playing, seamlessly restarts from the new position.
 * If stopped, starts playback from the target bar.
 */
export function seekToBar(bar, tracks, bpm, totalBars, playheadCallback, endCallback, loop = null, options = {}) {
  const clampedBar = Math.max(0, Math.min(bar, totalBars));
  startPlayback(tracks, bpm, totalBars, clampedBar, playheadCallback, endCallback, loop, options);
}

/**
 * Internal: stop all sources and cancel RAF without resetting state.
 */
function teardownSources() {
  for (const source of activeSources) {
    try {
      // Detach first. Stopping the end-of-song marker fires its onended a few ms
      // later, and that handler would stop the playback that is about to restart,
      // so every seek or live edit while playing went silent.
      source.onended = null;
      source.stop();
      source.disconnect();
    } catch (e) {
      // Source may have already ended
    }
  }
  activeSources = [];

  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

/**
 * Playhead update loop using requestAnimationFrame.
 * Reads AudioContext.currentTime and converts to bar position.
 */
function startPlayheadLoop(bpm, totalBars) {
  const spb = secondsPerBar(bpm);

  function tick() {
    if (!isPlaying) return;

    const ctx = getAudioContext();
    const elapsed = Math.max(playbackStartBar * spb, ctx.currentTime - contextStartTime);
    const linearBar = elapsed / spb;
    const currentBar = activeLoop
      ? activeLoop.startBar + ((linearBar - activeLoop.startBar) % (activeLoop.endBar - activeLoop.startBar) + (activeLoop.endBar - activeLoop.startBar)) % (activeLoop.endBar - activeLoop.startBar)
      : linearBar;

    if (!activeLoop && currentBar >= totalBars) {
      onPlayheadUpdate?.(totalBars);
      return;
    }

    onPlayheadUpdate?.(currentBar);
    rafId = requestAnimationFrame(tick);
  }

  rafId = requestAnimationFrame(tick);
}

/**
 * Update track volume in real-time (for slider changes during playback).
 */
export function setTrackVolume(trackId, volume) {
  currentTracks = currentTracks.map((track) => track.id === trackId ? { ...track, volume } : track);
  updateMixGraph(currentTracks, currentBpm);
}

/**
 * Update track pan in real-time.
 */
export function setTrackPan(trackId, pan) {
  currentTracks = currentTracks.map((track) => track.id === trackId ? { ...track, pan } : track);
  updateMixGraph(currentTracks, currentBpm);
}

/**
 * Update mute/solo state for all tracks during playback.
 */
export function updateSoloMute(tracks) {
  currentTracks = tracks;
  updateMixGraph(tracks, currentBpm);
}

/**
 * Cleanup all track nodes (on project unload).
 */
export function cleanup() {
  stopPlayback();
  currentTracks = [];
  updateMixGraph([], currentBpm);
}

/**
 * Is playback currently active?
 */
export function getIsPlaying() {
  return isPlaying;
}

/**
 * Is playback currently paused (with a saved position)?
 */
export function getIsPaused() {
  return isPaused;
}

/**
 * Get the bar where playback was paused. Returns 0 if not paused.
 */
export function getPausedBar() {
  return isPaused ? pausedAtBar : 0;
}

/** Sample-accurate transport state used by recording and count-in scheduling. */
export function getTransportPosition() {
  const ctx = getAudioContext();
  const spb = secondsPerBar(currentBpm);
  const elapsed = Math.max(playbackStartBar * spb, ctx.currentTime - contextStartTime);
  const linearBar = elapsed / spb;
  const bar = activeLoop
    ? activeLoop.startBar + ((linearBar - activeLoop.startBar) % (activeLoop.endBar - activeLoop.startBar) + (activeLoop.endBar - activeLoop.startBar)) % (activeLoop.endBar - activeLoop.startBar)
    : linearBar;
  return {
    playing: isPlaying,
    bar,
    contextTime: ctx.currentTime,
    startContextTime: scheduledStartContextTime,
    scheduledStartContextTime,
    loop: activeLoop ? { ...activeLoop } : null,
  };
}
